// src/components/tasksntickets/ticketTheme.js

export const RADIUS = {
  pill: 999,
  card: 10,
  dialog: 16,
  field: 8,
};

export const COLORS = {
  pageBg: "#f1f1ff",
  cardBg: "#FFFFFF",
  border: "#E7E7F0",
  borderStrong: "#D8D8E6",
  textPrimary: "#15161A",
  textSecondary: "#6B6C7A",
  textDisabled: "#A3A4B3",
  accent: "#6D5AE6", // indigo — primary action color (New ticket, Save changes, active pill)
  accentSoft: "#EFECFD",
  segmentActiveBg: "#15161A", // near-black pill for "Tickets" (vs "Projects")
  danger: "#DC2626",
  dangerSoft: "#FDECEC",
};

export const STATUS_COLUMNS = [
  { key: "open", label: "Open", color: "#7C3AED" },
  { key: "in_progress", label: "In Progress", color: "#6366F1" },
  { key: "in_review", label: "In Review", color: "#F59E0B" },
  { key: "done", label: "Done", color: "#22C55E" },
];

export const STATUS_LABEL = STATUS_COLUMNS.reduce((acc, s) => {
  acc[s.key] = s.label;
  return acc;
}, {});

export const PRIORITIES = [
  { value: "low", label: "Low", color: "#6B7280", soft: "#F3F4F6" },
  { value: "medium", label: "Medium", color: "#B45309", soft: "#FEF3C7" },
  { value: "high", label: "High", color: "#C2410C", soft: "#FFEDD5" },
  { value: "urgent", label: "Urgent", color: "#B91C1C", soft: "#FEE2E2" },
];

export const PRIORITY_MAP = PRIORITIES.reduce((acc, p) => {
  acc[p.value] = p;
  return acc;
}, {});

export const VISIBILITIES = [
  { value: "public", label: "Public" },
  { value: "personal", label: "Personal" },
];

/**
 * "7d left" / "Today" / "Overdue" — same rounded-pill copy the reference
 * design uses on ticket cards.
 */
export function formatDueDate(dueDate) {
  if (!dueDate) return null;

  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return null;

  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (startOfDay(due) - startOfDay(new Date())) / (1000 * 60 * 60 * 24)
  );

  if (diffDays < 0) return { text: "Overdue", tone: "overdue" };
  if (diffDays === 0) return { text: "Due today", tone: "soon" };
  if (diffDays === 1) return { text: "1d left", tone: "soon" };
  if (diffDays <= 3) return { text: `${diffDays}d left`, tone: "soon" };
  return { text: `${diffDays}d left`, tone: "normal" };
}

/** yyyy-mm-dd, the format a native <input type="date"> requires. */
export function formatDateInput(dueDate) {
  if (!dueDate) return "";
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * The single source of truth for "what color is this department" — mirrors
 * SideBar.jsx exactly: `dept.metadata?.bg` is where department colors
 * actually live and are kept in sync (that's why the sidebar dots are
 * always right), so it's checked FIRST. `dept.color` is only a fallback
 * for the rare department doc that has no metadata.bg yet — some
 * department records carry a stale/placeholder `color` field left over
 * from an older data shape, which was overriding the real color and
 * making several departments' tickets render as the same default purple.
 */
export function deptColorOf(dept) {
  return dept?.metadata?.bg || dept?.color || "#6366F1";
}

/**
 * Derives a { color, bg } pair for a department tag from that department's
 * assigned color (the same hex used for its sidebar dot / filter chip), so
 * a ticket's department tag always matches that department's color across
 * the whole app instead of a single fixed purple for every department.
 */
export function departmentTagStyle(hexColor) {
  const fallback = { color: "#5B21B6", bg: "#EDE9FE" };
  if (!hexColor || typeof hexColor !== "string") return fallback;

  const hex = hexColor.replace("#", "");
  if (![3, 6].includes(hex.length)) return fallback;

  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return fallback;

  return { color: hexColor, bg: `rgba(${r}, ${g}, ${b}, 0.12)` };
}

export function initialsOf(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
}

export const PROJECT_COLORS = [
  "#4F46E5",
  "#22C55E",
  "#EF4444",
  "#F59E0B",
  "#EC4899",
  "#06B6D4",
  "#8B5CF6",
  "#F97316",
  "#3B82F6",
  "#10B981",
];

export const TASK_STATUSES = [
  {
    value: "not_started",
    label: "Not started",
    color: "#6B7280",
    soft: "#F3F4F6",
  },
  {
    value: "in_progress",
    label: "In progress",
    color: "#6366F1",
    soft: "#EEF2FF",
  },
  { value: "in_review", label: "In review", color: "#F59E0B", soft: "#FEF3C7" },
  { value: "done", label: "Done", color: "#22C55E", soft: "#DCFCE7" },
];

export const TASK_STATUS_MAP = TASK_STATUSES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

/** "10-07-2026" — dd-mm-yyyy, matching the Projects table's Due Date column. */
export function formatDateDisplay(dueDate) {
  if (!dueDate) return "";
  const d = new Date(dueDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}
